import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { IconName } from '@ardoq/icons';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';
import { debounce } from 'lodash';
import { TextInput, TextInputProps } from './TextInput';
import { FormSize } from '../../types';

export type SearchInputProps = TextInputProps & {
  onValueChange: (value: string) => void;
  value?: string;
  debounceTime?: number;
  placeholder?: string;
  dataClickId?: string;
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      debounceTime = 250,
      onValueChange: onValueChangeProp,
      placeholder,
      dataClickId,
      dataTestId,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
      ...props
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState(value || '');

    const onValueChangeRef = useRef(onValueChangeProp);

    useEffect(() => {
      onValueChangeRef.current = onValueChangeProp;
    }, [onValueChangeProp]);

    const debouncedOnValueChange = useMemo(
      () =>
        debounce(
          (value: string) => onValueChangeRef.current(value),
          debounceTime
        ),
      [debounceTime]
    );

    useEffect(
      () => () => debouncedOnValueChange.cancel(),
      [debouncedOnValueChange]
    );

    const onValueChange = (newValue: string) => {
      setLocalValue(newValue);

      if (debounceTime > 0) {
        debouncedOnValueChange(newValue);
      } else {
        onValueChangeProp(newValue);
      }
    };

    const resolvedPlaceholder =
      placeholder ??
      t(i18nLocale, 'forms:searchInput.placeholder', 'Search...');

    return (
      <TextInput
        __DS_ONLY_fieldStyle={{ flexShrink: 0 }}
        formSize={FormSize.MEDIUM}
        placeholder={resolvedPlaceholder}
        aria-label={
          !props.label
            ? t(i18nLocale, 'forms:searchInput.ariaLabel', 'Search')
            : undefined
        }
        data-click-id={dataClickId ?? 'type-to-filter'}
        data-test-id={dataTestId ?? 'search-input'}
        leftIconName={IconName.SEARCH}
        ref={ref}
        value={localValue}
        onValueChange={onValueChange}
        onClear={() => onValueChange('')}
        i18nLocale={i18nLocale}
        {...props}
      />
    );
  }
);
SearchInput.displayName = 'SearchInput';
